'use client';
import { useEffect, useState } from 'react';
import { useAnalyticsSkuViewedContext } from '../Analytics/AnalyticsSkuViewed';
import { getFinishType } from '../Analytics/utils';
import type { LinkType, MonetizableEnum } from '../enums';

export interface MarketplacePayload {
  destination_url: string;
  element_position?: number;
  marketplace_institution_id?: string | null;
  marketplace_offer_id?: string | null;
  marketplace_product_id?: string | null;
  marketplace_product_type: string;
  monetizable: MonetizableEnum;
  section_name?: string;
  section_position?: number;
}

export interface GetCtaLinkProps {
  href: string;
  hasPrequalified?: boolean;
  type: LinkType;
  monetizable?: MonetizableEnum;
  /** The position of the product within the section, typically the index */
  elementPosition?: number;
  /** Details about where the apply link is located within the page */
  sectionName?: string;
  /**
   * Shopping Finish Type
   */
  finishType?: 'external_application' | 'internal_content' | 'lead_inputflow';
  shoppingFinishedId: string;
  /** Override the impression_section value from the SKU viewed context */
  impressionSectionOverride?: string;
}

export const useGetCtaLink = ({
  href,
  hasPrequalified = false,
  type,
  monetizable,
  elementPosition,
  finishType: finishTypeOverride,
  shoppingFinishedId,
  impressionSectionOverride,
}: GetCtaLinkProps): string => {
  const [path, setPath] = useState(href);
  const skuViewed = useAnalyticsSkuViewedContext();

  useEffect(() => {
    const finishType = finishTypeOverride
      ? finishTypeOverride
      : getFinishType(monetizable as MonetizableEnum);

    const isMonetizing = monetizable === 'yes_assumed';
    const defaultParams = isMonetizing
      ? {
          finish_type: finishType,
          has_prequalified: hasPrequalified,
          monetizable,
          element_position: elementPosition,
        }
      : {};

    if (href) {
      const { skuViewedPayload } = skuViewed;

      const parsedUrl = new URL(href);
      let props: Record<string, string | number | boolean | null | undefined> = {};

      if (finishType === 'external_application') {
        props = {
          ...defaultParams,
          sku_impression_id: skuViewedPayload?.sku_impression_id,
          impression_pageview_id: skuViewedPayload?.impression_pageview_id,
          impression_page_url: skuViewedPayload?.impression_page_url,
          impression_page_name: skuViewedPayload?.impression_page_name,
          impression_section: impressionSectionOverride ?? skuViewedPayload?.impression_section,
          impression_position: skuViewedPayload?.impression_position,
          finish_type: finishType,
          cta_type: type,
          monetizable,
          calling_deployable_name: skuViewedPayload?.deployable_name,
          calling_deployable_version: skuViewedPayload?.deployable_version,
          shopping_finish_id: shoppingFinishedId,
        };
      }

      Object.keys(props).forEach(key => {
        const obj = props[key];
        if (obj || obj === 0) {
          parsedUrl.searchParams.set(key, obj as never);
        }
      });

      setPath(parsedUrl.toString());
    }
  }, [href, skuViewed.skuViewedPayload, impressionSectionOverride]);

  return path;
};
